import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import { MinusOutlined, CloseOutlined } from '@ant-design/icons';
import { useMinimization } from '../../../../hooks/useMinimization';
import { useMapControls } from '../../../../context/MapControlsContext';
import './styles.css';

const MinimizableMapControl = ({
  id,
  title,
  icon,
  children,
  position = 'topright',
  defaultMinimized = false,
  closable = false,
  onClose,
  className = '',
  minimizedSize = 'medium',
  dock,
}) => {
  const { minimized, toggle, expand } = useMinimization(id, defaultMinimized);
  // MapControlsContext still manages order, but minimized controls now stack within Leaflet containers.
  useMapControls();

  // Position classes for expanded controls
  const POSITION_CLASSES = {
    bottomleft: 'leaflet-bottom leaflet-left',
    bottomright: 'leaflet-bottom leaflet-right',
    topleft: 'leaflet-top leaflet-left',
    topright: 'leaflet-top leaflet-right',
  };

  const positionClass = POSITION_CLASSES[position] || POSITION_CLASSES.topright;

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  if (minimized) {
    // Render into a bottom-center dock when requested
    if (dock === 'bottom-center') {
      const dockEl = typeof document !== 'undefined' ? document.getElementById('map-bottom-center-dock') : null;
      if (dockEl) {
        return ReactDOM.createPortal(
          <div className="map-bottom-center-dock-item">
            <div
              data-testid={id}
              className={`minimizable-control minimized leaflet-control ${minimizedSize} ${className}`}
              onClick={expand}
              title={`Expand ${title}`}
            >
              <div className="minimized-content">
                {icon && <span className="minimized-icon">{icon}</span>}
                <span className="minimized-title">{title}</span>
              </div>
            </div>
          </div>,
          dockEl
        );
      }
      // Fallback if dock is missing: use map corner container
    }
    return (
      <div className={positionClass}>
        <div
          data-testid={id}
          className={`minimizable-control minimized leaflet-control ${minimizedSize} ${className}`}
          onClick={expand}
          title={`Expand ${title}`}
        >
          <div className="minimized-content">
            {icon && <span className="minimized-icon">{icon}</span>}
            <span className="minimized-title">{title}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={positionClass}>
      <div data-testid={id} className={`minimizable-control expanded leaflet-control ${className}`}>
        <div className="control-header">
          <div className="control-title">
            {icon && <span className="control-icon">{icon}</span>}
            <span>{title}</span>
          </div>
          <div className="control-actions">
            <Button
              type="text"
              size="small"
              icon={<MinusOutlined />}
              onClick={toggle}
              title="Minimize"
              className="minimize-btn"
            />
            {closable && (
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={handleClose}
                title="Close"
                className="close-btn"
              />
            )}
          </div>
        </div>
        <div className="control-content">
          {children}
        </div>
      </div>
    </div>
  );
};

MinimizableMapControl.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  icon: PropTypes.node,
  children: PropTypes.node,
  position: PropTypes.oneOf(['topleft', 'topright', 'bottomleft', 'bottomright']),
  defaultMinimized: PropTypes.bool,
  closable: PropTypes.bool,
  onClose: PropTypes.func,
  className: PropTypes.string,
  minimizedSize: PropTypes.oneOf(['small', 'medium', 'large']),
  dock: PropTypes.oneOf([undefined, 'bottom-center']),
};

export default MinimizableMapControl;